import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, CalendarDays, Loader2, Plus, Minus, X, ChevronDown, ChevronUp, ChevronRight,
  AlertTriangle, Sparkles, RefreshCw, Lock, Pencil, Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useUsageStatus } from '../../components/ui/UsageStatus';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const CHANNEL_COLORS = {
  linkedin: '#0077B5', instagram: '#E4405F', facebook: '#1877F2', x: '#9AA5B1', pinterest: '#BD081C',
  blog: '#00D9C8', newsletter: '#F59E0B', pressemitteilung: '#8B9BB0',
};
const POST_TYPE_DE = { education: 'Bildung', opinion: 'Meinung', proof: 'Beweis', insight: 'Einblick', activation: 'Aktivierung' };
const POST_TYPE_EN = { education: 'Education', opinion: 'Opinion', proof: 'Proof', insight: 'Insight', activation: 'Activation' };

const errText = (err, fallback) => err?.response?.data?.detail || fallback;

const frequencyLabel = (pw, T) => {
  if (pw >= 14) return T('2× täglich', 'twice daily');
  if (pw === 7) return T('täglich (7× pro Woche)', 'daily (7× per week)');
  if (pw >= 1) return T(`${pw}× pro Woche`, `${pw}× per week`);
  if (pw >= 0.5) return T('alle 2 Wochen', 'every 2 weeks');
  if (pw > 0) return T('1× pro Monat', '1× per month');
  return T('pausiert', 'paused');
};

const FREQ_LADDER = [0.25, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];   // 14 pro Woche = 2× täglich

const nextFreq = (value, dir, max = 7) => {
  const ladder = FREQ_LADDER.filter((v) => v <= max);
  const idx = ladder.findIndex((v) => v >= value - 0.001);
  const cur = idx === -1 ? ladder.length - 1 : idx;
  return ladder[Math.max(0, Math.min(ladder.length - 1, cur + dir))];
};

const stepBtn = 'w-8 h-8 rounded bg-[#1A2B42] border border-[#2D3F59] text-white flex items-center justify-center hover:border-[#00D9C8]/60';

const FreqStepper = ({ value, max, onChange, T }) => (
  <div className="flex items-center gap-2">
    <button type="button" onClick={() => onChange(nextFreq(value, -1, max))} className={stepBtn} aria-label="weniger"><Minus className="w-4 h-4" /></button>
    <span className="text-white text-sm min-w-[150px] text-center">{frequencyLabel(value, T)}</span>
    <button type="button" onClick={() => onChange(nextFreq(value, 1, max))} className={stepBtn} aria-label="mehr"><Plus className="w-4 h-4" /></button>
  </div>
);

const STATUS_DE = { proposal: 'Entwurf', calendar_building: 'wird gebaut', calendar_ready: 'Kalender bereit', generating: 'wird erzeugt', paused: 'pausiert', generated: 'erzeugt', failed: 'fehlgeschlagen' };
const STATUS_EN = { proposal: 'Draft', calendar_building: 'building', calendar_ready: 'Calendar ready', generating: 'generating', paused: 'paused', generated: 'generated', failed: 'failed' };
const STATUS_COLOR = { calendar_ready: 'bg-[#0A1628] text-[#00D9C8]', generating: 'bg-[#0A1628] text-[#00D9C8]', generated: 'bg-[#0A1628] text-[#8AD9A0]', paused: 'bg-[#3A3220] text-[#F5D78E]', failed: 'bg-[#3B1F1F] text-[#F5B7B1]' };

const fmtShort = (iso, locale) => new Date(`${iso}T00:00:00`).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const ContentPlan = () => {
  const { getAuthHeader } = useAuth();
  const { lang } = useLanguage();
  const T = (de, en) => (lang === 'de' ? de : en);
  const locale = T('de-DE', 'en-GB');
  const navigate = useNavigate();
  const { limitReached } = useUsageStatus();
  const [searchParams] = useSearchParams();
  const POST_TYPE = lang === 'de' ? POST_TYPE_DE : POST_TYPE_EN;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [options, setOptions] = useState(null);
  const [plans, setPlans] = useState([]);                 // frühere Content-Kalender
  const [view, setView] = useState('list');               // 'list' | 'new' | 'plan'
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState('');                   // 'create' | 'calendar' | 'reduce' | 'approve' | 'resume'

  // Formular "neuer Kalender"
  const [days, setDays] = useState(() => {
    const d = parseInt(searchParams.get('days'), 10);
    return [7, 14, 30].includes(d) ? d : 30;
  });
  const [startDate, setStartDate] = useState('');
  const [startHint, setStartHint] = useState('');
  const [goal, setGoal] = useState('');                   // Wünsche des Nutzers, wörtlich
  const [selChannels, setSelChannels] = useState([]);     // {key, per_week}
  const [roles, setRoles] = useState([]);                 // parallele Rollen der Person (leer = nicht relevant)
  const [planRole, setPlanRole] = useState('combined');

  const adopt = useCallback((p) => { setPlan(p); }, []);

  useEffect(() => {
    axios.get(`${API}/projects/active/strategy`, { headers: getAuthHeader() })
      .then(({ data }) => setRoles(data.project_type === 'person' ? (data.roles || []) : []))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const loadPlans = useCallback(async () => {
    const { data } = await axios.get(`${API}/content-plan`, { headers: getAuthHeader() });
    setPlans(data.plans || []);
    return data.plans || [];
    // eslint-disable-next-line
  }, []);

  // Startdatum: nach dem letzten Kalender, sonst nächster Montag
  const suggestStart = (opt, list) => {
    const ends = list.filter((p) => ['calendar_building', 'calendar_ready', 'generating', 'paused', 'generated'].includes(p.status)).map((p) => p.end_date);
    const lastEnd = ends.sort().slice(-1)[0];
    if (lastEnd && addDays(lastEnd, 1) > opt.default_start_date) {
      setStartDate(addDays(lastEnd, 1));
      setStartHint(T(`Direkt nach deinem letzten Kalender (bis ${fmtShort(lastEnd, locale)})`, `Right after your last calendar (until ${fmtShort(lastEnd, locale)})`));
    } else {
      setStartDate(opt.default_start_date);
      setStartHint('');
    }
  };

  // ── Laden ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: opt } = await axios.get(`${API}/content-plan/options`, { headers: getAuthHeader() });
        if (cancelled) return;
        setOptions(opt);
        setDays((cur) => ((opt.allowed_days || opt.days).includes(cur) ? cur : (opt.allowed_days || opt.days)[0]));
        setSelChannels(opt.channels.filter((c) => c.enabled).slice(0, 3).map((c) => ({ key: c.key, per_week: c.default_per_week })));
        let list = [];
        try { list = await loadPlans(); } catch (e) { console.error('content-plan list failed', e); }   // die Liste darf die Seite nicht blockieren
        if (cancelled) return;
        suggestStart(opt, list);
        setView(searchParams.get('days') || list.length === 0 ? 'new' : 'list');
      } catch (err) {
        console.error('content-plan load failed', err);
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const reason = typeof detail === 'string' && detail
          ? detail
          : status
            ? T(`Der Plan-Assistent konnte nicht geladen werden (Fehler ${status}).`, `Could not load the plan assistant (error ${status}).`)
            : T(`Der Plan-Assistent konnte nicht geladen werden (${err?.message || 'unbekannter Fehler'}).`, `Could not load the plan assistant (${err?.message || 'unknown error'}).`);
        if (!cancelled) setLoadError(reason);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // ── Polling, solange gebaut oder erzeugt wird ──────────────────────────────
  const planId = plan?.id;
  const status = plan?.status;
  const pollRef = useRef(null);
  useEffect(() => {
    if (view !== 'plan' || !['calendar_building', 'generating'].includes(status) || !planId) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/content-plan/${planId}`, { headers: getAuthHeader() });
        if (data.plan.status !== status || status === 'generating') {
          adopt(data.plan);
          if (data.plan.status === 'failed') toast.error(data.plan.error || T('Der Kalender konnte nicht gebaut werden.', 'The calendar could not be built.'));
        } else {
          setPlan((prev) => (prev ? { ...prev, progress: data.plan.progress } : prev));
        }
      } catch (e) { /* nächster Versuch */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [view, status, planId]);

  // ── Aktionen ───────────────────────────────────────────────────────────────
  const startCalendar = async (p) => {
    const { data } = await axios.post(`${API}/content-plan/${p.id}/calendar`, {}, { headers: getAuthHeader() });
    setPlan({ ...p, status: data.status, progress: data.progress, slots: [], issues: [] });
    setView('plan');
  };

  // Ein Schritt für den Nutzer: Strategie im Hintergrund erstellen, dann direkt den Kalender bauen
  const createPlan = async () => {
    setBusy('create');
    try {
      const { data } = await axios.post(`${API}/content-plan/propose`, {
        days, start_date: startDate || null, goal: goal.trim() || null, channels: selChannels,
        role: roles.length >= 2 ? planRole : null,
      }, { headers: getAuthHeader(), timeout: 180000 });
      await startCalendar(data.plan);
    } catch (err) {
      toast.error(errText(err, T('Der Kalender konnte nicht erstellt werden.', 'Could not create the calendar.')));
    } finally { setBusy(''); }
  };

  const retryCalendar = async () => {
    setBusy('calendar');
    try { await startCalendar(plan); } catch (err) {
      toast.error(errText(err, T('Der Kalender konnte nicht gestartet werden.', 'Could not start the calendar.')));
    } finally { setBusy(''); }
  };

  const openPlan = async (id) => {
    setBusy(`open-${id}`);
    try {
      const { data } = await axios.get(`${API}/content-plan/${id}`, { headers: getAuthHeader() });
      adopt(data.plan);
      setView('plan');
    } catch (err) {
      toast.error(errText(err, T('Der Kalender konnte nicht geladen werden.', 'Could not load the calendar.')));
    } finally { setBusy(''); }
  };

  const openNew = () => { suggestStart(options, plans); setView('new'); setPlan(null); };
  const openList = () => { setView('list'); setPlan(null); loadPlans().catch(() => { /* Liste bleibt */ }); };

  const reduce = async () => {
    setBusy('reduce');
    try {
      const { data } = await axios.post(`${API}/content-plan/${plan.id}/reduce`, {}, { headers: getAuthHeader() });
      adopt(data.plan);
      await startCalendar(data.plan);
      toast.success(T('Frequenz reduziert, der Kalender wird neu gebaut.', 'Frequency reduced, rebuilding the calendar.'));
    } catch (err) {
      toast.error(errText(err, T('Reduzieren fehlgeschlagen.', 'Reducing failed.')));
    } finally { setBusy(''); }
  };

  const approve = async () => {
    const n = plan.slots.length;
    const cost = plan.credits?.text;
    if (!window.confirm(T(`Plan freigeben? Das kostet ${cost ? `${cost} Credits` : 'Credits'} für ${n} Beiträge (Texte). Bilder werden erst berechnet, wenn du sie im Beitrag anforderst. Jetzt erzeugen?`,
                          `Approve plan? This costs ${cost ? `${cost} credits` : 'credits'} for ${n} posts (texts). Images are only charged when you request them in a post. Generate now?`))) return;
    setBusy('approve');
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin';
      const { data } = await axios.post(`${API}/content-plan/${plan.id}/approve`, { timezone: tz }, { headers: getAuthHeader() });
      adopt(data.plan);
    } catch (err) {
      toast.error(errText(err, T('Der Plan konnte nicht freigegeben werden.', 'Could not approve the plan.')));
    } finally { setBusy(''); }
  };

  const resume = async () => {
    setBusy('resume');
    try {
      const { data } = await axios.post(`${API}/content-plan/${plan.id}/resume`, {}, { headers: getAuthHeader() });
      adopt(data.plan);
    } catch (err) {
      toast.error(errText(err, T('Fortsetzen nicht möglich.', 'Could not resume.')));
    } finally { setBusy(''); }
  };

  const retryFailed = async () => {
    setBusy('retry');
    try {
      const { data } = await axios.post(`${API}/content-plan/${plan.id}/retry-failed`, {}, { headers: getAuthHeader() });
      adopt(data.plan);
    } catch (err) {
      toast.error(errText(err, T('Neu erzeugen nicht möglich.', 'Could not retry.')));
    } finally { setBusy(''); }
  };

  const channelLabel = (key) => options?.channels?.find((c) => c.key === key)?.label || key;

  // ── Render ─────────────────────────────────────────────────────────────────
  const header = (
    <div className="mb-6">
      {view === 'list' || plans.length === 0 ? (
        <Link to="/app/kreation" className="inline-flex items-center gap-1.5 text-sm text-[#8B9BB0] hover:text-[#00D9C8] mb-3">
          <ArrowLeft className="w-4 h-4" /> {T('Zurück zur Kreation', 'Back to creation')}
        </Link>
      ) : (
        <button onClick={openList} className="inline-flex items-center gap-1.5 text-sm text-[#8B9BB0] hover:text-[#00D9C8] mb-3">
          <ArrowLeft className="w-4 h-4" /> {T('Meine Content-Kalender', 'My content calendars')}
        </button>
      )}
      <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-white mb-1 flex items-center gap-3" data-testid="content-plan-title">
        <CalendarDays className="w-7 h-7 text-[#00D9C8]" /> {T('Content für die nächsten Tage', 'Content for the coming days')}
      </h1>
      <p className="text-sm lg:text-base text-[#A0AEC0]">
        {T('Kanäle und Zeitraum wählen, Kalender prüfen, freigeben. Erst dann wird Content erzeugt.',
           'Choose channels and period, review the calendar, approve. Only then content is generated.')}
      </p>
    </div>
  );

  if (loading) {
    return <div className="flex items-center gap-2 text-[#A0AEC0]"><Loader2 className="w-5 h-5 animate-spin" />{T('Lade …', 'Loading …')}</div>;
  }
  if (loadError) {
    return (
      <div className="max-w-3xl">
        {header}
        <Card className="bg-[#1A2B42] border-transparent"><CardContent className="p-5 text-[#F5B7B1] flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" /><span>{loadError}</span>
        </CardContent></Card>
      </div>
    );
  }

  // Übersicht der bisherigen Kalender
  if (view === 'list') {
    return (
      <div className="max-w-3xl" data-testid="content-plan-list">
        {header}
        <Button onClick={openNew} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold w-full sm:w-auto mb-6" data-testid="plan-new-btn">
          <Plus className="w-4 h-4 mr-2" />{T('Neuen Content-Kalender erstellen', 'Create new content calendar')}
        </Button>
        <div className="text-xs uppercase tracking-wide text-[#6B7A94] font-semibold mb-3">{T('Deine Kalender', 'Your calendars')}</div>
        <div className="space-y-3">
          {plans.map((p) => {
            const label = (lang === 'de' ? STATUS_DE : STATUS_EN)[p.status] || p.status;
            const c = p.counts || {};
            return (
              <button key={p.id} onClick={() => openPlan(p.id)} disabled={!!busy}
                className="w-full text-left bg-[#1A2B42] hover:bg-[#1F3350] rounded-lg p-4 flex items-center justify-between gap-3 transition-colors" data-testid={`plan-card-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-white font-semibold">{p.days} {T('Tage', 'days')} · {fmtShort(p.start_date, locale)} {T('bis', 'to')} {fmtShort(p.end_date, locale)}</span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${STATUS_COLOR[p.status] || 'bg-[#0A1628] text-[#8B9BB0]'}`}>{label}</span>
                  </div>
                  <div className="text-sm text-[#8B9BB0]">
                    {c.total || 0} {T('Beiträge', 'posts')}
                    {c.generated > 0 && ` · ${c.generated} ${T('erzeugt', 'generated')}`}
                    {c.approved > 0 && ` · ${c.approved} ${T('freigegeben', 'approved')}`}
                  </div>
                </div>
                {busy === `open-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin text-[#00D9C8] shrink-0" /> : <ChevronRight className="w-5 h-5 text-[#6B7A94] shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Neuer Kalender: Rahmen
  if (view === 'new') {
    return (
      <div className="max-w-3xl" data-testid="content-plan-setup">
        {header}
        <Card className="bg-[#1A2B42] border-transparent">
          <CardContent className="p-5 space-y-6">
            <div>
              <label className="block text-sm text-[#A0AEC0] mb-2">{T('Zeitraum', 'Period')}</label>
              <div className="flex gap-2">
                {options.days.map((d) => {
                  const allowed = (options.allowed_days || options.days).includes(d);
                  return (
                    <button key={d} onClick={() => allowed && setDays(d)} disabled={!allowed} data-testid={`plan-days-${d}`}
                      title={allowed ? undefined : T('Ab einem Paket verfügbar', 'Available with a plan')}
                      className={`px-5 py-2 rounded border text-sm font-semibold transition-all inline-flex items-center gap-1.5 ${!allowed
                        ? 'bg-[#0A1628] text-[#5B6B80] border-[#1F2F45] cursor-not-allowed opacity-70'
                        : days === d ? 'bg-[#00D9C8] text-[#0A1628] border-[#00D9C8]' : 'bg-[#0A1628] text-white border-[#2D3F59] hover:border-[#00D9C8]/50'}`}>
                      {!allowed && <Lock className="w-3.5 h-3.5" />}{d} {T('Tage', 'days')}
                    </button>
                  );
                })}
              </div>
              {options.is_trial && (
                <div className="mt-3 rounded-lg border border-[#00D9C8]/30 bg-[#00D9C8]/5 px-4 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2" data-testid="plan-trial-note">
                  <div className="text-sm text-[#B7C4D4]">
                    <span className="font-semibold text-white">{T('Dein Test: 1 Woche.', 'Your trial: 1 week.')}</span>{' '}
                    {T('Mit einem Paket planst du gleich den ganzen Monat.', 'With a plan you can plan the whole month.')}
                  </div>
                  <Button onClick={() => navigate('/app/upgrade')} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold h-9" data-testid="plan-trial-upgrade-btn">
                    {T('Paket wählen', 'Choose a plan')}
                  </Button>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#A0AEC0] mb-2">{T('Startdatum', 'Start date')}</label>
                <Input type="date" value={startDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => { setStartDate(e.target.value); setStartHint(''); }}
                  className="bg-[#0A1628] border-transparent text-white" data-testid="plan-start-date" />
                {startHint && <p className="text-xs text-[#6B7A94] mt-1.5">{startHint}</p>}
              </div>
            </div>
            <div data-testid="plan-setup-channels">
              <label className="block text-sm text-[#A0AEC0] mb-2">{T('Kanäle und wie oft pro Woche', 'Channels and how often per week')}</label>
              <div className="space-y-2">
                {selChannels.map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-3 flex-wrap p-3 bg-[#0A1628] rounded" data-testid={`plan-setup-channel-${c.key}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: CHANNEL_COLORS[c.key] || '#8B9BB0' }} />
                      <span className="text-white font-semibold">{channelLabel(c.key)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FreqStepper value={c.per_week} max={options.channels.find((o) => o.key === c.key)?.max_per_week} T={T}
                        onChange={(v) => setSelChannels((cur) => cur.map((x) => (x.key === c.key ? { ...x, per_week: v } : x)))} />
                      <button onClick={() => setSelChannels((cur) => cur.filter((x) => x.key !== c.key))} className="ml-1 text-[#6B7A94] hover:text-[#F5B7B1]" aria-label="entfernen"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                {selChannels.length === 0 && <p className="text-sm text-[#F5D78E]">{T('Bitte wähle mindestens einen Kanal.', 'Please choose at least one channel.')}</p>}
              </div>
              {options.channels.some((c) => c.enabled && !selChannels.some((x) => x.key === c.key)) && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="text-xs text-[#6B7A94]">{T('Kanal hinzufügen:', 'Add channel:')}</span>
                  {options.channels.filter((c) => c.enabled && !selChannels.some((x) => x.key === c.key)).map((c) => (
                    <button key={c.key} onClick={() => setSelChannels((cur) => [...cur, { key: c.key, per_week: c.default_per_week }])}
                      className="px-2.5 py-1 text-xs rounded border border-[#2D3F59] text-[#A0AEC0] hover:border-[#00D9C8]/60 hover:text-[#00D9C8]">+ {c.label}</button>
                  ))}
                </div>
              )}
            </div>
            {roles.length >= 2 && (
              <div data-testid="plan-role">
                <label className="block text-sm text-[#A0AEC0] mb-2">{T('Für welche Rolle?', 'For which role?')}</label>
                <div className="flex flex-wrap gap-2">
                  {[{ id: 'combined', label: T('Kombiniert', 'Combined') }, ...roles.map(r => ({ id: r.id, label: [r.job, r.company].filter(Boolean).join(' @ ') }))].map(o => (
                    <button type="button" key={o.id} onClick={() => setPlanRole(o.id)} className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${planRole === o.id ? 'border-[#00D9C8] bg-[#00D9C8]/15 text-white' : 'border-[#2A3B52] text-[#A0AEC0] hover:text-white'}`} data-testid={`plan-role-${o.id}`}>{o.label}</button>
                  ))}
                </div>
                <p className="text-xs text-[#6B7A94] mt-1.5">{planRole === 'combined'
                  ? T('Der Kalender verbindet deine Rollen. Für getrennte Kalender erstelle einen pro Rolle.', 'The calendar connects your roles. For separate calendars, create one per role.')
                  : T('Der Kalender behandelt nur diese Rolle.', 'The calendar covers this role only.')}</p>
              </div>
            )}
            <div>
              <label className="block text-sm text-[#A0AEC0] mb-2">{T('Besondere Wünsche (optional)', 'Special requests (optional)')}</label>
              <Textarea value={goal} onChange={(e) => setGoal(e.target.value)} maxLength={1500}
                placeholder={T('Was soll unbedingt vorkommen? z. B. Webinar am 20.10., neues Produkt, Rabattaktion, ein bestimmtes Thema oder Ziel',
                               'What must be included? e.g. webinar on 20 Oct, new product, discount campaign, a specific topic or goal')}
                className="min-h-[96px] bg-[#0A1628] border-transparent text-white placeholder:text-[#8B9BB0] resize-none" data-testid="plan-goal" />
              <p className="text-xs text-[#6B7A94] mt-1.5">{T('Das übernehmen wir in den Kalender, bei Terminen mit Vorlauf und Nachbereitung. Ohne Angabe leiten wir alles aus deiner Brand DNA ab.',
                                                              'We build this into the calendar, with lead-up and follow-up for dates. Without input we derive everything from your Brand DNA.')}</p>
            </div>
            <div className="flex justify-end">
              <Button onClick={createPlan} disabled={busy === 'create' || selChannels.length === 0 || limitReached} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold" data-testid="plan-propose-btn">
                {busy === 'create'
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{T('Kalender wird vorbereitet …', 'Preparing calendar …')}</>
                  : <><Sparkles className="w-4 h-4 mr-2" />{T('Kalender erstellen', 'Create calendar')}</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!plan) return null;   // Übergang beim Wechsel der Ansicht

  // Kalender wird gebaut
  if (plan.status === 'calendar_building') {
    const done = plan.progress?.done || 0;
    const total = plan.progress?.total || 1;
    return (
      <div className="max-w-3xl" data-testid="content-plan-building">
        {header}
        <Card className="bg-[#1A2B42] border-transparent"><CardContent className="p-6">
          <div className="flex items-center gap-3 text-white mb-4">
            <Loader2 className="w-5 h-5 animate-spin text-[#00D9C8]" />
            <span className="font-semibold">{T('Dein Kalender wird gebaut …', 'Building your calendar …')}</span>
          </div>
          <div className="h-2 bg-[#0A1628] rounded overflow-hidden mb-2">
            <div className="h-full bg-[#00D9C8] transition-all" style={{ width: `${Math.max(6, (done / total) * 100)}%` }} />
          </div>
          <p className="text-sm text-[#A0AEC0]">{T(`Woche ${Math.min(done + 1, total)} von ${total}. Das dauert etwa 1 bis 3 Minuten, du kannst die Seite offen lassen.`,
                                                    `Week ${Math.min(done + 1, total)} of ${total}. This takes about 1 to 3 minutes, you can keep this page open.`)}</p>
        </CardContent></Card>
      </div>
    );
  }

  // Aufbau fehlgeschlagen oder nie gestartet
  if (plan.status === 'proposal' || plan.status === 'failed') {
    return (
      <div className="max-w-3xl" data-testid="content-plan-retry">
        {header}
        <Card className="bg-[#1A2B42] border-transparent"><CardContent className="p-5">
          {plan.status === 'failed' && (
            <div className="flex items-start gap-2 text-[#F5B7B1] text-sm mb-4"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{plan.error || T('Der Kalender konnte nicht gebaut werden.', 'The calendar could not be built.')}</span></div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={retryCalendar} disabled={!!busy} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold" data-testid="plan-build-calendar-btn">
              {busy === 'calendar' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-2" />}
              {plan.status === 'failed' ? T('Erneut versuchen', 'Try again') : T('Kalender erstellen', 'Create calendar')}
            </Button>
            <button onClick={openNew} className="text-sm text-[#6B7A94] hover:text-white">{T('Neuer Kalender', 'New calendar')}</button>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  // Kalender
  return <CalendarStep {...{ plan, options, T, POST_TYPE, header, channelLabel, reduce, restart: openNew, navigate, busy, adopt, getAuthHeader, approve, resume, retryFailed, limitReached }} />;
};

const CalendarStep = ({ plan, options, T, POST_TYPE, header, channelLabel, reduce, restart, navigate, busy, adopt, getAuthHeader, approve, resume, retryFailed, limitReached }) => {
  const locked = ['generating', 'paused', 'generated'].includes(plan.status);
  const doneCount = plan.slots.filter((s) => s.generation === 'done').length;
  const failedCount = plan.slots.filter((s) => s.generation === 'failed').length;
  const failedErrors = [...new Set(plan.slots.filter((s) => s.generation === 'failed' && s.generation_error).map((s) => s.generation_error))];
  const gen = plan.generation || {};
  const weeks = plan.strategy?.weeks || [];
  const credits = plan.credits;

  const byWeek = useMemo(() => {
    const m = {};
    plan.slots.forEach((s) => { (m[s.week_index] = m[s.week_index] || []).push(s); });
    return m;
  }, [plan.slots]);

  const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(T('de-DE', 'en-GB'),
    { weekday: 'short', day: '2-digit', month: '2-digit' });

  return (
    <div className="max-w-4xl space-y-6" data-testid="content-plan-calendar">
      {header}
      <Card className="bg-[#1A2B42] border-transparent"><CardContent className="p-4 lg:p-5">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="min-w-0">
            <div className="text-white font-semibold text-lg">{plan.slots.length} {T('Beiträge in', 'posts in')} {plan.days} {T('Tagen', 'days')}</div>
          </div>
          {!locked && (
            <div className="flex items-center gap-4">
              <button onClick={restart} className="text-sm text-[#6B7A94] hover:text-white flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" />{T('Neuer Plan', 'New plan')}</button>
              <Button onClick={approve} disabled={!!busy || limitReached || (credits && credits.missing > 0)}
                className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold disabled:opacity-50" data-testid="plan-approve-btn">
                {busy === 'approve' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {T('Plan freigeben und generieren', 'Approve and generate plan')}
              </Button>
            </div>
          )}
        </div>

        {!locked && credits && credits.missing > 0 && (
          <div className="mt-4 p-4 rounded bg-[#3B1F1F]" data-testid="plan-credits-missing">
            <div className="flex items-start gap-2 text-[#F5B7B1]">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{T(`Du brauchst noch ${credits.missing} Credits, um den Plan so zu generieren.`, `You still need ${credits.missing} credits to generate this plan.`)}</span>
            </div>
            <div className="mt-3 flex flex-col items-start gap-2">
              <Button onClick={() => navigate('/app/upgrade')} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold">{T('Credits hinzufügen', 'Add credits')}</Button>
              <button onClick={reduce} disabled={!!busy} className="text-sm text-[#8B9BB0] hover:text-white underline-offset-2 hover:underline">
                {busy === 'reduce' ? T('Wird reduziert …', 'Reducing …') : T('Contentplan reduzieren', 'Reduce content plan')}
              </button>
            </div>
          </div>
        )}

        {locked && (
          <div className="mt-4 p-4 rounded bg-[#0A1628]" data-testid={`plan-generation-${plan.status}`}>
            {plan.status === 'generating' && (
              <>
                <div className="flex items-center gap-2 text-white font-semibold mb-2"><Loader2 className="w-4 h-4 animate-spin text-[#00D9C8]" />
                  {T(`Woche ${Math.min((gen.weeks_done || 0) + 1, gen.weeks_total || 1)} von ${gen.weeks_total || 1} wird erzeugt …`,
                     `Generating week ${Math.min((gen.weeks_done || 0) + 1, gen.weeks_total || 1)} of ${gen.weeks_total || 1} …`)}</div>
                <div className="h-2 bg-[#1A2B42] rounded overflow-hidden mb-2">
                  <div className="h-full bg-[#00D9C8] transition-all" style={{ width: `${Math.max(5, (doneCount / Math.max(plan.slots.length, 1)) * 100)}%` }} />
                </div>
                <div className="text-xs text-[#8B9BB0]">{doneCount} {T('von', 'of')} {plan.slots.length} {T('Beiträgen fertig. Du kannst schon mit dem Freigeben beginnen.', 'posts done. You can already start approving.')}</div>
              </>
            )}
            {plan.status === 'paused' && (
              <>
                <div className="flex items-start gap-2 text-[#F5B7B1] mb-3"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{plan.error || T('Die Erzeugung wurde unterbrochen.', 'Generation was interrupted.')}</span></div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={resume} disabled={!!busy} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold">
                    {busy === 'resume' ? <Loader2 className="w-4 h-4 animate-spin" /> : T('Fortsetzen', 'Resume')}
                  </Button>
                  <Button variant="ghost" onClick={() => navigate('/app/upgrade')} className="text-[#00D9C8] border border-[#00D9C8]/30">{T('Credits hinzufügen', 'Add credits')}</Button>
                </div>
              </>
            )}
            {plan.status === 'generated' && (
              <div className="text-white font-semibold">
                {failedCount === 0
                  ? T(`Alle ${doneCount} Beiträge sind erzeugt.`, `All ${doneCount} posts are generated.`)
                  : T(`${doneCount} von ${plan.slots.length} Beiträgen sind erzeugt.`, `${doneCount} of ${plan.slots.length} posts are generated.`)}
                {failedCount > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <span className="text-[#F5B7B1] font-normal text-sm">{failedCount} {T('sind fehlgeschlagen.', 'failed.')}{failedErrors[0] && ` ${T('Grund', 'Reason')}: ${failedErrors[0]}`}</span>
                    <Button onClick={retryFailed} disabled={!!busy} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold" data-testid="plan-retry-btn">
                      {busy === 'retry' ? <Loader2 className="w-4 h-4 animate-spin" /> : T('Fehlgeschlagene neu erzeugen', 'Retry failed posts')}
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className="mt-3">
              <Button onClick={() => navigate(`/app/produktion?plan=${plan.id}`)} disabled={doneCount === 0} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold disabled:opacity-50" data-testid="plan-review-btn">
                <CalendarDays className="w-4 h-4 mr-2" />{T('Beiträge durchgehen und freigeben', 'Review and approve posts')}
              </Button>
            </div>
          </div>
        )}
      </CardContent></Card>

      <div className="space-y-10 pt-2">
      {Object.keys(byWeek).sort((a, b) => a - b).map((wi) => {
        const w = weeks.find((x) => String(x.index) === String(wi)) || {};
        return (
          <div key={wi}>
            <WeekHeader w={w} wi={wi} plan={plan} T={T} adopt={adopt} getAuthHeader={getAuthHeader} locked={locked} />
            <div className="space-y-3">
              {byWeek[wi].map((s) => (
                <SlotRow key={s.slot_id} slot={s} plan={plan} T={T} POST_TYPE={POST_TYPE} channelLabel={channelLabel} fmtDate={fmtDate}
                  adopt={adopt} getAuthHeader={getAuthHeader} locked={locked} />
              ))}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

const WeekHeader = ({ w, wi, plan, T, adopt, getAuthHeader, locked }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState('');
  const [headline, setHeadline] = useState('');
  const [topic, setTopic] = useState('');

  const start = () => { setHeadline(w.headline || ''); setTopic(w.hub_topic || ''); setEditing(true); };
  const save = async (rewrite) => {
    setBusy(rewrite ? 'rewrite' : 'save');
    try {
      const { data } = await axios.put(`${API}/content-plan/${plan.id}/weeks/${wi}`,
        { headline, hub_topic: topic, rewrite }, { headers: getAuthHeader(), timeout: 180000 });
      adopt(data.plan);
      setEditing(false);
      toast.success(rewrite ? T('Beiträge der Woche neu geschrieben.', 'Posts of the week rewritten.') : T('Wochenthema gespeichert.', 'Week topic saved.'));
    } catch (err) {
      toast.error(errText(err, T('Das hat nicht geklappt.', 'That did not work.')));
    } finally { setBusy(''); }
  };

  if (editing) {
    return (
      <div className="mb-4 p-4 rounded-lg bg-[#1A2B42]" data-testid={`week-edit-${wi}`}>
        <div className="text-xs uppercase tracking-wide text-[#00D9C8] font-semibold mb-3">{T('Woche', 'Week')} {wi}{w.phase ? ` · ${w.phase}` : ''}</div>
        <Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder={T('Überschrift', 'Headline')}
          className="mb-2 bg-[#0A1628] border-transparent text-white h-9" />
        <Textarea value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={T('Kernthema der Woche', 'Core topic of the week')}
          className="min-h-[64px] bg-[#0A1628] border-transparent text-white text-sm resize-none mb-3" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save(true)} disabled={!!busy} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold h-9 text-sm" data-testid={`week-rewrite-${wi}`}>
            {busy === 'rewrite' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{T('Wird neu geschrieben …', 'Rewriting …')}</> : T('Speichern und Beiträge neu schreiben', 'Save and rewrite posts')}
          </Button>
          <Button variant="ghost" onClick={() => save(false)} disabled={!!busy} className="text-[#00D9C8] border border-[#00D9C8]/30 h-9 text-sm">
            {busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : T('Nur Thema speichern', 'Save topic only')}
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)} disabled={!!busy} className="text-[#A0AEC0] h-9 text-sm">{T('Abbrechen', 'Cancel')}</Button>
        </div>
        <p className="text-xs text-[#6B7A94] mt-2">{T('Beim Neuschreiben bleiben Beiträge, die du selbst bearbeitet hast, unverändert.', 'Posts you edited yourself stay unchanged when rewriting.')}</p>
      </div>
    );
  }

  return (
    <div className="mb-3 px-1 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-[#00D9C8] font-semibold mb-0.5">{T('Woche', 'Week')} {wi}{w.phase ? ` · ${w.phase}` : ''}</div>
        {(w.hub_topic || w.headline) && <div className="text-sm text-[#A0AEC0]">{w.hub_topic || w.headline}</div>}
      </div>
      {!locked && (
        <button onClick={start} className="shrink-0 p-1.5 rounded text-[#6B7A94] hover:text-[#00D9C8] hover:bg-[#1A2B42]" aria-label={T('Wochenthema ändern', 'Edit week topic')} data-testid={`week-edit-btn-${wi}`}>
          <Pencil className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const SlotRow = ({ slot: s, plan, T, POST_TYPE, channelLabel, fmtDate, adopt, getAuthHeader, locked }) => {
  const [mode, setMode] = useState('view');          // 'view' | 'input' | 'edit' | 'regen'
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({});
  const [more, setMore] = useState(false);
  const [hint, setHint] = useState('');
  const [userInput, setUserInput] = useState(s.user_input || '');

  const lastDay = new Date(`${plan.start_date}T00:00:00`);
  lastDay.setDate(lastDay.getDate() + plan.days - 1);
  const maxDate = lastDay.toISOString().slice(0, 10);
  const tappable = !locked && s.needs_input;
  const base = `${API}/content-plan/${plan.id}/slots/${s.slot_id}`;
  const fieldCls = 'bg-[#0A1628] border-transparent text-white text-sm';

  const call = async (name, fn, okMsg) => {
    setBusy(name);
    try {
      const { data } = await fn();
      adopt(data.plan);
      if (okMsg) toast.success(okMsg);
      setMode('view');
    } catch (err) {
      toast.error(errText(err, T('Das hat nicht geklappt.', 'That did not work.')));
    } finally { setBusy(''); }
  };

  const startEdit = () => {
    setForm({
      topic: s.topic || '', core_message: s.core_message || '', date: s.date, time: s.time,
      angle: s.angle || '', hook: s.structure?.hook || '', body: s.structure?.body || '', close: s.structure?.close || '',
      cta: s.cta || '', keywords: (s.keywords || []).join(', '),
    });
    setMore(false);
    setMode('edit');
  };
  const save = () => call('save', () => axios.put(base, {
    ...form, keywords: form.keywords.split(',').map((k) => k.trim()).filter(Boolean),
  }, { headers: getAuthHeader() }), T('Gespeichert.', 'Saved.'));
  const saveInput = () => call('input', () => axios.put(base, { user_input: userInput }, { headers: getAuthHeader() }),
    T('Angabe übernommen.', 'Input saved.'));
  const regenerate = () => call('regen', () => axios.post(`${base}/regenerate`, { hint: hint.trim() || null },
    { headers: getAuthHeader(), timeout: 180000 }), T('Beitrag neu erzeugt.', 'Post regenerated.'));
  const remove = () => {
    if (!window.confirm(T('Diesen Beitrag aus dem Plan entfernen?', 'Remove this post from the plan?'))) return;
    call('delete', () => axios.delete(base, { headers: getAuthHeader() }), T('Beitrag entfernt.', 'Post removed.'));
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const iconBtn = 'w-8 h-8 rounded flex items-center justify-center text-[#6B7A94] hover:text-[#00D9C8] hover:bg-[#0A1628] transition-colors';

  const content = (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded bg-[#0A1628] text-white">
          <span className="w-2 h-2 rounded-full" style={{ background: CHANNEL_COLORS[s.channel] || '#8B9BB0' }} />{channelLabel(s.channel)}
        </span>
        <span className="text-xs text-[#8B9BB0]">{POST_TYPE[s.post_type]}</span>
        {s.needs_input && <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#3A3220] text-[#F5D78E]">{T('Input nötig', 'Input needed')}</span>}
        {s.generation === 'generating' && <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#0A1628] text-[#00D9C8]">{T('wird erzeugt', 'generating')}</span>}
        {s.generation === 'done' && <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#0A1628] text-[#8AD9A0]">{T('erzeugt', 'created')}</span>}
        {s.generation === 'failed' && <span title={s.generation_error || ''} className="text-[11px] px-1.5 py-0.5 rounded bg-[#3B1F1F] text-[#F5B7B1]">{T('fehlgeschlagen', 'failed')}</span>}
      </div>
      <div className="text-white text-sm font-medium">{s.topic || '—'}</div>
      {s.generation === 'failed' && s.generation_error && <div className="text-xs text-[#F5B7B1] mt-1 break-words">{T('Grund', 'Reason')}: {s.generation_error}</div>}
    </>
  );

  return (
    <div className="bg-[#1A2B42] rounded" data-testid={`plan-slot-${s.slot_id}`}>
      <div className="relative p-4 flex flex-col sm:flex-row items-start gap-1.5 sm:gap-3">
        <div className="sm:w-[88px] shrink-0 flex sm:block items-baseline gap-2">
          <div className="text-white text-sm font-semibold">{fmtDate(s.date)}</div>
          <div className="text-xs text-[#8B9BB0]">{s.time}</div>
        </div>
        {tappable ? (
          <button onClick={() => setMode(mode === 'input' ? 'view' : 'input')} className="flex-1 min-w-0 text-left flex items-start gap-2 pr-28 sm:pr-0" data-testid={`slot-tap-${s.slot_id}`}>
            <div className="flex-1 min-w-0">{content}</div>
            {mode === 'input' ? <ChevronUp className="w-4 h-4 text-[#F5D78E] shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-[#F5D78E] shrink-0 mt-1" />}
          </button>
        ) : (
          <div className="flex-1 min-w-0 pr-28 sm:pr-0">{content}</div>
        )}
        {!locked && mode === 'view' && (
          <div className="absolute top-3 right-3 sm:static flex items-center gap-0.5 shrink-0">
            <button onClick={startEdit} className={iconBtn} aria-label={T('Bearbeiten', 'Edit')} title={T('Bearbeiten', 'Edit')} data-testid={`slot-edit-btn-${s.slot_id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => setMode('regen')} className={iconBtn} aria-label={T('Neu erzeugen', 'Regenerate')} title={T('Neu erzeugen', 'Regenerate')}><RefreshCw className="w-4 h-4" /></button>
            <button onClick={remove} disabled={!!busy} className={`${iconBtn} hover:text-[#F5B7B1]`} aria-label={T('Entfernen', 'Remove')} title={T('Entfernen', 'Remove')}>
              {busy === 'delete' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>

      {mode === 'input' && tappable && (
        <div className="px-4 pb-4" data-testid={`slot-missing-${s.slot_id}`}>
          <div className="p-3 rounded bg-[#3A3220] text-[#F5D78E]">
            <div className="text-xs font-semibold mb-1">{T('Dafür brauchen wir von dir:', 'We need from you:')}</div>
            <ul className="list-disc pl-4 text-xs space-y-0.5 mb-3">{(s.missing_evidence || []).map((m, i) => <li key={i}>{m}</li>)}</ul>
            <Textarea value={userInput} onChange={(e) => setUserInput(e.target.value)} maxLength={1000}
              placeholder={T('Deine Angabe, z. B. die Zahl, das Erlebnis oder das Zitat', 'Your input, e.g. the number, the experience or the quote')}
              className="min-h-[56px] bg-[#0A1628] border-transparent text-white text-sm resize-none mb-2" />
            <Button onClick={saveInput} disabled={!!busy || !userInput.trim()} className="bg-[#F5D78E] text-[#0A1628] hover:bg-[#F0CC73] h-8 text-xs font-bold">
              {busy === 'input' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : T('Angabe übernehmen', 'Save input')}
            </Button>
          </div>
        </div>
      )}

      {mode === 'regen' && (
        <div className="px-4 pb-4">
          <div className="p-3 rounded bg-[#0A1628]">
            <Input value={hint} onChange={(e) => setHint(e.target.value)} maxLength={400}
              placeholder={T('Hinweis (optional), z. B. „persönlicher“ oder „mit Fokus auf Kosten“', 'Hint (optional), e.g. "more personal" or "focus on costs"')}
              className="bg-[#1A2B42] border-transparent text-white text-sm h-9 mb-2" />
            <div className="flex gap-2">
              <Button onClick={regenerate} disabled={!!busy} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold h-8 text-xs" data-testid={`slot-regen-go-${s.slot_id}`}>
                {busy === 'regen' ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{T('Wird neu geschrieben …', 'Rewriting …')}</> : T('Neu erzeugen', 'Regenerate')}
              </Button>
              <Button variant="ghost" onClick={() => setMode('view')} disabled={!!busy} className="text-[#A0AEC0] h-8 text-xs">{T('Abbrechen', 'Cancel')}</Button>
            </div>
          </div>
        </div>
      )}

      {mode === 'edit' && (
        <div className="px-4 pb-4 space-y-2.5" data-testid={`slot-edit-${s.slot_id}`}>
          <EditField label={T('Titel', 'Title')}><Input value={form.topic} onChange={set('topic')} className={fieldCls} /></EditField>
          <EditField label={T('Kernaussage', 'Core message')}><Textarea value={form.core_message} onChange={set('core_message')} className={`${fieldCls} min-h-[60px] resize-none`} /></EditField>
          <div className="grid grid-cols-2 gap-2 max-w-sm">
            <EditField label={T('Datum', 'Date')}><Input type="date" value={form.date} min={plan.start_date} max={maxDate} onChange={set('date')} className={fieldCls} /></EditField>
            <EditField label={T('Uhrzeit', 'Time')}><Input type="time" value={form.time} onChange={set('time')} className={fieldCls} /></EditField>
          </div>
          <button onClick={() => setMore((m) => !m)} className="text-xs text-[#8B9BB0] hover:text-[#00D9C8] flex items-center gap-1">
            {more ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}{T('Weitere Felder', 'More fields')}
          </button>
          {more && (
            <div className="space-y-2.5">
              <EditField label={T('Winkel', 'Angle')}><Input value={form.angle} onChange={set('angle')} className={fieldCls} /></EditField>
              <EditField label="Hook"><Textarea value={form.hook} onChange={set('hook')} className={`${fieldCls} min-h-[48px] resize-none`} /></EditField>
              <EditField label={T('Verlauf', 'Flow')}><Textarea value={form.body} onChange={set('body')} className={`${fieldCls} min-h-[72px] resize-none`} /></EditField>
              <EditField label={T('Schluss', 'Close')}><Textarea value={form.close} onChange={set('close')} className={`${fieldCls} min-h-[48px] resize-none`} /></EditField>
              <EditField label="CTA"><Input value={form.cta} onChange={set('cta')} className={fieldCls} /></EditField>
              {['longform', 'press'].includes(s.kind) && (
                <EditField label={T('Keywords (mit Komma)', 'Keywords (comma separated)')}><Input value={form.keywords} onChange={set('keywords')} className={fieldCls} /></EditField>
              )}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={!!busy} className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00C4B4] font-bold h-9" data-testid={`slot-save-${s.slot_id}`}>
              {busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : T('Speichern', 'Save')}
            </Button>
            <Button variant="ghost" onClick={() => setMode('view')} disabled={!!busy} className="text-[#A0AEC0] h-9">{T('Abbrechen', 'Cancel')}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const EditField = ({ label, children }) => (
  <div><div className="text-xs text-[#6B7A94] mb-1">{label}</div>{children}</div>
);

export default ContentPlan;
